/* globals views, showModalError, showModalConfirm, rulesRate */
import React, { useContext, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import SelectableTable, { Column } from "common/SelectableTable";
import { safeStr, getQuantileStats } from "common/api";
import useProfile from "common/hooks/useProfile";

const TableContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: auto;
  .dataTable tbody td.warning{
    background: #cc0000;
    color: #fff;
  }
  &.colored {
    .dataTable tbody td {
      &.warning {
        background: transparent;
      }
      &.hyperlink-text {
        cursor: pointer;
      }
      &.min-q1 {
        background: #3d85c6;
        color: #fff;
      }
      &.q1-median {
        background: #6aa84f;
        color: #fff;
      }
      &.q3-max {
        background: #cc0000;
        color: #fff;
      }
      &.median-q3 {
        background: #e69138;
        color: #fff;
      }
    }
  }
`;

const dynamicPrecisionRanges = [
  [/*below*/ 10, 3 /*digits*/],
  [/*below*/ 100, 2 /*digits*/],
  [/*below*/ 1000, 1 /*digits*/],
  // Defaults to 0 digits
];
const doesNothing = () => {};

const applyProfileRestrictions = ({ hideRates = false }, columns) =>
  hideRates !== true
    ? columns
    : columns.map((column) =>
        column.label === "RATE-POLICY"
          ? {
              ...column,
              cellClassName: null,
              onClick: null,
            }
          : column.label === "LIM-Mbps"
          ? Column.Value(() => "n/a", {
              label: "LIM-Mbps",
              title: "LIM-Mbps",
              idx: 8,
              colClassName: "align-right",
            })
          : column
      );

const ListSubscriberGroups = ({
  onPlotSelection = doesNothing,
  onRemoved = doesNothing,
  timePeriod,
  metric,
  direction,
  tableData,
  renderTable,
  setRenderTable,
  statisticsData,
  colorize,
  thresholds
}) => {
  const actions = useContext(ActionsContext);

  useEffect(()=>{
    setTimeout(() => {
      setRenderTable(true);
    }, 250);
  }, [renderTable]);

  useEffect(()=>{
    setRenderTable(false)
    setTimeout(() => {
      setRenderTable(true);
    }, 250);
  }, [JSON.stringify(thresholds)])

  const profile = useProfile();
  if (profile === null) {
    return null;
  }

  const [header, ...rows] =  tableData.trim("\n").split("\n");
  const metricsQuantileData = Object.entries(statisticsData).reduce(
    (acc, [key, values]) => {
      if (key !== "totalGroups" && key !== "newRows" && key !== "names") {
        const validValues = values.reduce((accValues, value) => {
          if (value !== "n/a" && value !== null) {
            accValues.push(parseFloat(value));
          }
          return accValues;
        }, []);
        const quantileData = getQuantileStats(validValues);
        acc[key] = quantileData;
      }
      return acc;
    },
    {}
  );

  const getQuantileClass= (_value, quantiles, inverted, groupName)=>{
    if (!quantiles || _value === 'n/a' || _value === null || groupName === "all-subscribers") {
      return "";
    }
    const { q1, q3, median, min, max } = quantiles;
    if(!inverted){
      if (_value > q3 && _value <= max) {
        return "q3-max";
      }
      if (_value > median && _value <= q3) {
        return "median-q3";
      }
      if (_value > q1 && _value <= median) {
        return "q1-median";
      }
      if (_value >= min && _value <= q1) {
        return "min-q1";
      }
      return "";
    } else {
      if (_value > q3 && _value <= max) {
        return "min-q1";
      }
      if (_value > median && _value <= q3) {
        return "q1-median";
      }
      if (_value > q1 && _value <= median) {
        return "median-q3";
      }
      if (_value >= min && _value <= q1) {
        return "q3-max";
      }
      return "";
    }
   
  }

  const getWarningClass = (threshold, value, mean, meanValue, inverted, label) => {
    if (mean && meanValue < mean || label === "MEAN-Mbps") {
      return "";
    }
    if(!inverted){
      if (threshold && value > threshold) {
        return "warning";
      }
      return "";
    }
    if (threshold && value < threshold) {
      return "warning";
    }

    return "";
  };

  const quartileColorCellClassName = (_value, _row, column) => {
    const { quantiles, label, threshold, mean, inverted } = column;
    const groupName = _row[0];
    const hyperlinkClass = label === "SUBS-ACTIVE" ? "hyperlink-text" : "";
    const warningClass = getWarningClass(threshold, _value, mean, _row[9], inverted, label);
    const quantileClass = getQuantileClass(_value, quantiles, inverted, groupName);
    return `${hyperlinkClass} ${warningClass} ${quantileClass}`;
  };

  const tableColumns = [
    Column.Text({
      label: "SUBSCRIBER-GROUP",
      idx: 0,
      title: "Subscriber’s group",
      cellClassName: "hyperlink-text",
      onClick: (subscriberGroup, row) => {
        globalNavigate("viewStatusSubscriberGroups", {
          subscriberGroup,
          timePeriod,
          metric,
          direction,
          returnView: "viewStatusSubscriberGroups",
          type: row[13],
        });
      },
    }),
    Column.Number({
      label: "SUBS-ACTIVE",
      idx: 2,
      title: "Subscribers active",
      colClassName: "align-right",
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["SUBS-ACTIVE"],
      onClick: (_, row) => {
        globalNavigate("viewStatusSubscriberGroups", {
          subscriberGroup: row[0],
          timePeriod,
          metric,
          view: "qoe",
          direction,
          type: row[13],
          returnView: "viewStatusSubscriberGroups",
        });
      },
    }),
    Column.Number({
      label: "FL-ACTIVE",
      title: "FL Active",
      idx: 3,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "LIM-Mbps",
      title: "LIM-Mbps",
      idx: 8,
      precision: dynamicPrecisionRanges,
      colClassName: "align-right",
    }),
    Column.Text({
      label: "RATE-POLICY",
      idx: 9,
      title: "RATE-POLICY",
      cellClassName: (value, row) =>
        `align-left${value !== "n/a" ? " hyperlink-text" : ""}${
          row[6] === "undefined" ? " col-red  cell-with-icon-left" : ""
        }`,
      onClick: (_policyName, row, _trigger) => {
        const [_name, _active, _flows, _limit, policyName] = row;
        const assignment = row[14];
        policyName !== "n/a"
          ? actions.send("subscriber-group-policy-rate-clicked", {
              policyName,
              assignment,
            })
          : null;
      },
      icon: (row) => {
        const assignment = row[14];
        return assignment === "undefined" ? "warning" : null;
      },
    }),
    Column.Number({
      label: "FL-CREATED",
      title: "FL Created",
      idx: 4,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "MBYTES",
      title: "MBytes",
      precision: dynamicPrecisionRanges,
      idx: 5,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "CURR-Mbps",
      title: "Current speed in Mbps",
      idx: 6,
      colClassName: "align-right",
    }),
    Column.NumberOrNA({
      label: "MEAN-Mbps",
      title:
        "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
      idx: 7,
      colClassName: "align-right",
      precision: dynamicPrecisionRanges,
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["MEAN-Mbps"],
      inverted: true,
    }),
    Column.NumberOrNA({
      label: "RTT-ms",
      title:
        "Moving average of latency, in milliseconds. If downlink, latency from this node to subs, if uplink, latency from this node to Internet server",
      idx: 11,
      colClassName: "align-right",
      precision: dynamicPrecisionRanges,
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["RTT-ms"],
      threshold: thresholds["RTT-ms"],
      mean: thresholds["MEAN-Mbps"],
      inverted: false,
    }),
    Column.NumberOrNA({
      label: "RTX",
      title:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",

      idx: 12,
      percent: true,
      precision: 1,
      colClassName: "align-right",
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["RTX"],
      threshold: thresholds["RTX"],
      mean: thresholds["MEAN-Mbps"],
      inverted: false,
    }),
    Column.NumberOrNA({
      label: "CONGESTION",
      field: "CONGEST",
      title: "Moving average of percentage of traffic suffering congestion",
      idx: 14,
      percent: true,
      colClassName: "align-right",
      cellClassName: quartileColorCellClassName,
      quantiles: metricsQuantileData["CONGEST"],
      threshold: thresholds["CONGEST"],
      mean: thresholds["MEAN-Mbps"],
      inverted: false,
    }),
    Column.Time({
      label: "LIFETIME",
      title: "Duration so far of this subscriber session",
      idx: 15,
      colClassName: "align-right",
    }),
    Column.Text({
      label: "GROUP-TYPE",
      title: "Subscriber group type",
      idx: 16,
    }),
    Column.Hidden({
      label: "ASSIGNMENT",
      idx: 10,
    }),
    Column.Actions({
      label: "ACTIONS",
      idx: 0,
      colClassName: "text-center action three-actions",
      cellClassName: "hides-content-to-operators",
      are: [
        {
          id: "edit-subs-group",
          label: "Edit",
          icon: "edit",
          onClick: (_, [subscriberGroup]) =>
            actions.send("subscriber-group-edit-clicked", {
              subscriberGroup,
            }),
        },
        {
          id: "throughput-subs-group",
          label: "Thoughput",
          icon: "search",
          onClick: (_, row) =>
            globalNavigate("viewStatusSubscriberGroups", {
              subscriberGroup: row[0],
              view: "throughput",
              timePeriod,
              metric,
              direction,
              type: row[13],
            }),
        },
        {
          id: "delete-subscriber-group",
          icon: "delete",
          label: "Delete",
          onClick: (subscribersGroup) => {
            showModalConfirm(
              `Delete ${subscribersGroup} subsciber group.`,
              "Deleting it will erase its statistics.",
              "delete_forever",
              () => {
                ifCl
                  .run(
                    `clear api subscriber-group ${safeStr(subscribersGroup)}`
                  )
                  .then(() => onRemoved(subscribersGroup))
                  .catch((error) => showModalError(error));
              }
            );
          },
        },
      ],
    }),
  ];

  if (!renderTable) {
    return null;
  }

  const tableClassName = colorize ? "colored" : "notColored";

  return (
    <TableContainer className={tableClassName}>
      <SelectableTable
        pageLength={50}
        selectionField={"plot"}
        selectionLabel={"PLOT"}
        asObject={([name]) => ({ name })}
        onSelection={(selected) => onPlotSelection(selected)}
        selectFirst={25}
        exportAsCSV={true}
        content={tableData}
        columns={applyProfileRestrictions(profile, tableColumns)}
        rowsLength={rows.length}
      />
    </TableContainer>
  );
};

export default ListSubscriberGroups;
